/**
 * This function delays the call of a function to prevent unnecessary rapid calls
 * @param fn The original, non debounced function (You can pass any number of args to it)
 * @param delay The delay (in ms) for the function to return
 * @returns The debounced function, which will run only if the debounced function has not been called in the last (delay) ms
 */
export function debounce<F extends (...args: any[]) => any>(
  fn: F,
  delay: number = 200
) {
  let timeoutTimerId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<F>) => {
    clearTimeout(timeoutTimerId);

    timeoutTimerId = setTimeout(() => {
      // @ts-expect-error
      fn.apply(this, args);
    }, delay);
  };
}

/**
 * This function asynchrously delays the call of a function to prevent unnecessary rapid calls
 * @param fn The original, non debounced function (You can pass any number of args to it)
 * @param delay The delay (in ms) for the function to return
 * @returns The debounced function, which will run only if the debounced function has not been called in the last (delay) ms
 */
export function debounceAsync<F extends (...args: any[]) => any>(
  fn: F,
  delay: number = 200
) {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>): Promise<ReturnType<F>> => {
    if (timer) clearTimeout(timer);
    return new Promise((resolve) => {
      timer = setTimeout(async () => {
        resolve(await fn(...args));
      }, delay);
    });
  };
}
